import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { apiUrl } from "../../utils/apiUrl";
import { lightenHexColor, themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import "../game/game.css";
import "./feedback.css";

const FEEDBACK_OPTIONS = [
  { id: "amazing", label: "Amazingggg", emoji: "🤩" },
  { id: "loved", label: "Loved it", emoji: "😍" },
  { id: "fun", label: "It was fun", emoji: "😊" },
  { id: "okay", label: "It was okay", emoji: "👍" },
];

const CONFETTI_MS = 2000;
const CONFETTI_COUNT = 64;

function buildImageConfettiPieces() {
  const ts = Date.now();
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

export default function Feedback() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackStatusLoaded, setFeedbackStatusLoaded] = useState(false);
  const [hadSavedFeedback, setHadSavedFeedback] = useState(false);
  const [confettiOn, setConfettiOn] = useState(false);
  const [confettiSrc, setConfettiSrc] = useState("");
  const [confettiPieces, setConfettiPieces] = useState(null);

  const popupBg = String(themeData?.popup_background_color || "#1B1433").trim() || "#1B1433";
  const popupText = String(themeData?.popup_text_color || "#ffffff").trim() || "#ffffff";
  const btnBg = themeButtonBackground(themeData) || "#3B82F6";
  const btnColor = themeButtonTextColor(themeData) || "#ffffff";
  const tileBg = lightenHexColor(popupBg, 0.12) || "rgba(255,255,255,0.08)";
  const showLeaderboard = String(themeData?.show_leaderboard ?? "true").trim().toLowerCase() !== "false";

  useEffect(() => {
    if (!user) return;
    dispatch(setBackButtonUrl("/leaderboard"));
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) return;
    if (!adminToken) {
      setFeedbackStatusLoaded(true);
      return;
    }
    let cancelled = false;
    const labels = FEEDBACK_OPTIONS.map((o) => o.label);
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw && labels.includes(String(raw).trim())) {
          setSelected(String(raw).trim());
          setHadSavedFeedback(true);
        }
      } catch (e) {
        console.error("spotGameStatus (feedback)", e);
      } finally {
        if (!cancelled) setFeedbackStatusLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const saveFeedback = async (label) => {
    if (!adminToken || submitting) return;
    setSubmitting(true);
    setSelected(label);
    try {
      const resp = await fetch(apiUrl("/saveUserFeedback"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId,
          sessionId: user.sessionId,
          organizationId: user.organizationId,
          userFeedback: label,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.success === false) {
        await Swal.fire({
          icon: "error",
          title: "Could not save",
          text: data.error || resp.statusText || "Try again later",
        });
        setSelected(null);
        setSubmitting(false);
        return;
      }

      setHadSavedFeedback(true);
      const opt = FEEDBACK_OPTIONS.find((o) => o.label === label);
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (opt?.emoji && !reduceMotion) {
        setConfettiSrc(opt.emoji);
        setConfettiPieces(buildImageConfettiPieces());
        setConfettiOn(true);
        window.setTimeout(() => {
          setConfettiOn(false);
          setConfettiPieces(null);
          setConfettiSrc("");
        }, CONFETTI_MS);
      }
    } catch (e) {
      console.error("saveUserFeedback", e);
      await Swal.fire({ icon: "error", title: "Network error", text: String(e.message) });
      setSelected(null);
      setSubmitting(false);
    }
  };

  if (themeStatus !== "succeeded" || !themeData || !feedbackStatusLoaded) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className="coc-page fb-page"
      style={{
        "--fb-card-bg": popupBg,
        "--fb-card-text": popupText,
        "--fb-tile-bg": tileBg,
        "--fb-btn-bg": btnBg,
        "--fb-btn-text": btnColor,
      }}
      aria-label="Feedback"
    >
      <div className="fb-card">
        <div className="fb-banner">Thank You!</div>
        <h1 className="fb-headline">How was your experience? Tap an emoji to share your thoughts</h1>

        {hadSavedFeedback ? (
          <p className="fb-hint">You already sent feedback — tap an option to update it.</p>
        ) : null}

        <section className="fb-options" aria-label="Feedback options">
          {FEEDBACK_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`fb-option${selected === opt.label ? " fb-option--selected" : ""}`}
              disabled={submitting}
              onClick={() => saveFeedback(opt.label)}
            >
              <span className="fb-option-emoji" aria-hidden>
                {opt.emoji}
              </span>
              <span className="fb-option-label">{opt.label}</span>
            </button>
          ))}
        </section>

        <p className="fb-foot">Your feedback helps us improve!</p>
      </div>

      {showLeaderboard ? (
        <button type="button" className="fb-leaderboard-btn" onClick={() => navigate("/leaderboard")}>
          <span aria-hidden>🏆</span> Leaderboard
        </button>
      ) : null}

      {confettiOn && confettiSrc && confettiPieces ? (
        <div className="fb-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.key}
              className="fb-confetti-img"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: p.size,
                "--fb-dx": `${p.dx}px`,
                "--fb-dy": `${p.dy}px`,
                "--fb-rot": `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            >
              {confettiSrc}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
